import React, { useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { ArrowLeft, ArrowRight, HelpCircle } from 'lucide-react';
import { OnboardingState } from '@/hooks/useOnboarding';
import { FloatingInput } from './FloatingInput';
import { motion } from 'framer-motion';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

const SEGMENTOS = [
  'Infraestrutura',
  'Construção Civil',
  'Saneamento',
  'Energia',
  'Habitação',
  'Outro',
];

const FUSOS = [
  'America/Sao_Paulo',
  'America/Manaus',
  'America/Belem',
  'America/Fortaleza',
  'America/Recife',
  'America/Noronha',
];

function formatCnpj(value: string): string {
  const d = value.replace(/\D/g, '').slice(0, 14);
  return d
    .replace(/^(\d{2})(\d)/, '$1.$2')
    .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1/$2')
    .replace(/(\d{4})(\d)/, '$1-$2');
}

interface ConfigProps {
  state: OnboardingState;
  onUpdate: (patch: Partial<OnboardingState>) => void;
  onNext: () => void;
  onBack: () => void;
  validateCnpj: (cnpj: string) => boolean;
}

const item = {
  hidden: { opacity: 0, y: 10 },
  show: { opacity: 1, y: 0 },
};

export const OnboardingConfig: React.FC<ConfigProps> = ({
  state, onUpdate, onNext, onBack, validateCnpj,
}) => {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const validate = useCallback((): boolean => {
    const errs: Record<string, string> = {};
    if (!state.empresa_nome.trim()) errs.empresa_nome = 'Nome da empresa é obrigatório';
    if (state.empresa_nome.trim().length > 150) errs.empresa_nome = 'Máximo 150 caracteres';
    if (state.cnpj && !validateCnpj(state.cnpj)) errs.cnpj = 'CNPJ inválido';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }, [state.empresa_nome, state.cnpj, validateCnpj]);

  // Real-time validation on blur
  const handleBlur = (field: string) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    validate();
  };

  const handleNext = () => {
    setTouched({ empresa_nome: true, cnpj: true });
    if (validate()) onNext();
  };

  const empresaValid = touched.empresa_nome && state.empresa_nome.trim().length > 0 && !errors.empresa_nome;
  const cnpjValid = touched.cnpj && state.cnpj.length > 0 && validateCnpj(state.cnpj);

  return (
    <motion.div
      className="space-y-5"
      initial="hidden"
      animate="show"
      variants={{ show: { transition: { staggerChildren: 0.06 } } }}
    >
      <motion.div variants={item}>
        <h2 className="text-xl font-semibold">Configuração Inicial</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Informe os dados básicos. Tudo pode ser ajustado depois.
        </p>
      </motion.div>

      <div className="space-y-4">
        <motion.div variants={item}>
          <FloatingInput
            label="Nome da empresa"
            placeholder=" "
            maxLength={150}
            value={state.empresa_nome}
            onChange={e => onUpdate({ empresa_nome: e.target.value })}
            onBlur={() => handleBlur('empresa_nome')}
            error={touched.empresa_nome ? errors.empresa_nome : undefined}
            valid={empresaValid}
          />
        </motion.div>

        <motion.div variants={item}>
          <FloatingInput
            label="CNPJ"
            optional
            placeholder=" "
            value={state.cnpj}
            onChange={e => onUpdate({ cnpj: formatCnpj(e.target.value) })}
            onBlur={() => handleBlur('cnpj')}
            error={touched.cnpj ? errors.cnpj : undefined}
            valid={cnpjValid}
            tooltip="Se preenchido, será validado e deve ser único no sistema."
            maxLength={18}
          />
        </motion.div>

        <motion.div variants={item} className="space-y-1.5">
          <Label className="text-sm font-medium">Segmento de atuação</Label>
          <Select value={state.segmento} onValueChange={v => onUpdate({ segmento: v })}>
            <SelectTrigger className="h-12 rounded-xl">
              <SelectValue placeholder="Selecione o segmento" />
            </SelectTrigger>
            <SelectContent>
              {SEGMENTOS.map(s => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </motion.div>

        <motion.div variants={item} className="space-y-1.5">
          <Label className="text-sm font-medium">Fuso horário</Label>
          <Select value={state.fuso_horario} onValueChange={v => onUpdate({ fuso_horario: v })}>
            <SelectTrigger className="h-12 rounded-xl">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {FUSOS.map(f => (
                <SelectItem key={f} value={f}>{f.replace('America/', '')}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </motion.div>

        {/* Alertas */}
        <motion.div variants={item} className="space-y-2">
          <div className="flex items-center gap-1.5">
            <Label className="text-sm font-medium">
              Alerta de Quality Gate —{' '}
              <strong className="text-primary">{state.qg_alert_days} dia{state.qg_alert_days !== 1 ? 's' : ''}</strong>
            </Label>
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <HelpCircle className="w-3.5 h-3.5 text-muted-foreground/50" />
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-[240px] text-xs">
                  Notifica quando uma execução concluída não teve Quality Gate iniciado após este prazo.
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <Slider
            min={1} max={30} step={1}
            value={[state.qg_alert_days]}
            onValueChange={([v]) => onUpdate({ qg_alert_days: v })}
          />
        </motion.div>

        <motion.div variants={item} className="space-y-2">
          <div className="flex items-center gap-1.5">
            <Label className="text-sm font-medium">
              Alerta de medição —{' '}
              <strong className="text-primary">{state.medicao_alert_days} dia{state.medicao_alert_days !== 1 ? 's' : ''}</strong>
            </Label>
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <HelpCircle className="w-3.5 h-3.5 text-muted-foreground/50" />
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-[240px] text-xs">
                  Notifica quando um item executado não foi incluído em boletim de medição.
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <Slider
            min={1} max={60} step={1}
            value={[state.medicao_alert_days]}
            onValueChange={([v]) => onUpdate({ medicao_alert_days: v })}
          />
        </motion.div>
      </div>

      <motion.div variants={item} className="flex gap-3">
        <Button variant="outline" onClick={onBack} className="flex-1 group">
          <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" />
          Voltar
        </Button>
        <Button onClick={handleNext} className="flex-1 group">
          Revisar e confirmar
          <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
        </Button>
      </motion.div>
    </motion.div>
  );
};
